import { useEffect, useState } from 'react';
import { Plus, Search, Edit3, Trash2, Mail, Phone, X, Briefcase, Activity } from 'lucide-react';
import { crmApi, Contato } from '../crmApi';
import EtiquetasPicker from '../EtiquetasPicker';
import CamposCustomForm from '../CamposCustomForm';
import EmpresaContaPicker from '../EmpresaContaPicker';
import ResponsavelPicker from '../ResponsavelPicker';
import CardDrawer from '../CardDrawer';

interface Props { session: any; }

const TIPOS: { value: Contato['tipo']; label: string }[] = [
  { value: 'cliente', label: 'Cliente' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'parceiro', label: 'Parceiro' },
  { value: 'fornecedor', label: 'Fornecedor' },
];

const CRMContatos = ({ session }: Props) => {
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [minhaCarteira, setMinhaCarteira] = useState(true);
  const [carregando, setCarregando] = useState(false);
  const [editando, setEditando] = useState<Contato | null>(null);
  const [drawerContato, setDrawerContato] = useState<Contato | null>(null);
  const [novo, setNovo] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try { setContatos(await crmApi.listarContatos(session)); }
    catch (e: any) { alert('Erro: ' + e.message); }
    finally { setCarregando(false); }
  };
  useEffect(() => { carregar(); }, []); // eslint-disable-line

  const meuEmail = session?.usuario?.email || '';
  const filtrados = contatos.filter(c => {
    const q = busca.toLowerCase().trim();
    const matchQ = !q || c.nome.toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q) || (c.empresa || '').toLowerCase().includes(q) || (c.telefone || '').includes(q);
    const matchT = !filtroTipo || c.tipo === filtroTipo;
    const matchC = !minhaCarteira || (c.responsavelId === meuEmail);
    return matchQ && matchT && matchC;
  });

  const remover = async (c: Contato) => {
    if (!confirm(`Excluir contato "${c.nome}"?`)) return;
    await crmApi.removerContato(session, c.id);
    carregar();
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contatos</h1>
          <p className="text-sm text-gray-600">Cadastro central de clientes, prospects, parceiros e fornecedores</p>
        </div>
        <button onClick={() => setNovo(true)} className="bg-black text-white px-3 py-2 rounded-lg flex items-center gap-1.5 hover:bg-gray-800 text-sm">
          <Plus className="h-4 w-4" /> Novo Contato
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nome, email, empresa, telefone…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white">
          <option value="">Todos os tipos</option>
          {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setMinhaCarteira(true)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${minhaCarteira ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`}>
            Minha carteira
          </button>
          <button onClick={() => setMinhaCarteira(false)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!minhaCarteira ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`}>
            Toda a equipe
          </button>
        </div>
      </div>

      {carregando && <div className="text-sm text-gray-500">Carregando…</div>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-xs text-gray-600 uppercase">
              <th className="text-left px-4 py-2">Nome</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Empresa / Cargo</th>
              <th className="text-left px-4 py-2 hidden sm:table-cell">Contato</th>
              <th className="text-left px-4 py-2">Tipo</th>
              <th className="px-4 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {filtrados.length === 0 && !carregando && (
              <tr><td colSpan={5} className="text-center py-12 text-gray-500 text-sm">Nenhum contato encontrado.</td></tr>
            )}
            {filtrados.map(c => (
              <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2">
                  <button onClick={() => setEditando(c)} className="font-medium text-gray-900 hover:underline text-left">{c.nome}</button>
                </td>
                <td className="px-4 py-2 text-sm text-gray-700 hidden md:table-cell">
                  {c.empresa && <div className="flex items-center gap-1 text-xs"><Briefcase className="h-3 w-3" /> {c.empresa}</div>}
                  {c.cargo && <div className="text-[11px] text-gray-500">{c.cargo}</div>}
                </td>
                <td className="px-4 py-2 text-xs text-gray-600 hidden sm:table-cell">
                  {c.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</div>}
                  {c.telefone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" /> {c.telefone}</div>}
                </td>
                <td className="px-4 py-2">
                  <span className="text-[11px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full capitalize">{c.tipo}</span>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => setDrawerContato(c)} title="Atividades / WhatsApp"
                    className="p-1 hover:bg-blue-50 rounded text-blue-700 mr-1"><Activity className="h-4 w-4" /></button>
                  <button onClick={() => setEditando(c)} className="p-1 hover:bg-gray-100 rounded text-gray-600"><Edit3 className="h-4 w-4" /></button>
                  <button onClick={() => remover(c)} className="p-1 hover:bg-red-50 rounded text-red-600 ml-1"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(novo || editando) && (
        <ContatoModal session={session} contato={editando || undefined}
          onClose={() => { setNovo(false); setEditando(null); }}
          onSaved={() => { setNovo(false); setEditando(null); carregar(); }} />
      )}

      <CardDrawer session={session} open={!!drawerContato} onClose={() => setDrawerContato(null)}
        vinculo={drawerContato ? { contatoId: drawerContato.id } : {}}
        titulo={drawerContato?.nome || ''}
        subtitulo={drawerContato?.empresa || drawerContato?.cargo || ''}
        telefone={drawerContato?.telefone}
        responsavelId={drawerContato?.responsavelId} />
    </div>
  );
};

const ContatoModal = ({ session, contato, onClose, onSaved }: { session: any; contato?: Contato; onClose: () => void; onSaved: () => void }) => {
  const [nome, setNome] = useState(contato?.nome || '');
  const [email, setEmail] = useState(contato?.email || '');
  const [telefone, setTelefone] = useState(contato?.telefone || '');
  const [empresa, setEmpresa] = useState(contato?.empresa || '');
  const [empresaContaId, setEmpresaContaId] = useState<string | null>(contato?.empresaContaId || null);
  const [cargo, setCargo] = useState(contato?.cargo || '');
  const [tipo, setTipo] = useState<Contato['tipo']>(contato?.tipo || 'prospect');
  const [responsavelId, setResponsavelId] = useState<string | null>(contato?.responsavelId || (session?.usuario?.email || null));
  const [observacoes, setObservacoes] = useState(contato?.observacoes || '');
  const [etiquetas, setEtiquetas] = useState<string[]>(contato?.etiquetas || []);
  const [camposCustom, setCamposCustom] = useState<Record<string, any>>(contato?.camposCustom || {});
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!nome.trim()) { alert('Nome obrigatório'); return; }
    setSalvando(true);
    try {
      const payload = { nome: nome.trim(), email, telefone, empresa, empresaContaId, cargo, tipo, responsavelId, observacoes, etiquetas, camposCustom };
      if (contato) await crmApi.atualizarContato(session, contato.id, payload);
      else await crmApi.criarContato(session, payload);
      onSaved();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-semibold text-gray-900">{contato ? 'Editar Contato' : 'Novo Contato'}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs text-gray-600">Nome *</label>
            <input value={nome} onChange={e => setNome(e.target.value)} autoFocus
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          {/* Linha 2: E-mail | Telefone | Empresa */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600">E-mail</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Telefone</label>
              <input value={telefone} onChange={e => setTelefone(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Empresa</label>
              <input value={empresa} onChange={e => setEmpresa(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
          {/* Linha 3: Cargo | Tipo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Cargo</label>
              <input value={cargo} onChange={e => setCargo(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Tipo</label>
              <select value={tipo} onChange={e => setTipo(e.target.value as Contato['tipo'])}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          {/* Empresa (Conta) */}
          <EmpresaContaPicker session={session} value={empresaContaId} onChange={(id) => setEmpresaContaId(id)} />
          <ResponsavelPicker session={session} value={responsavelId} onChange={setResponsavelId} />
          {/* Linha 4: Observações + Etiquetas */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Observações</label>
              <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <EtiquetasPicker session={session} value={etiquetas} onChange={setEtiquetas} />
            </div>
          </div>
          <CamposCustomForm session={session} entidade="contato" value={camposCustom} onChange={setCamposCustom} />
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-700">Cancelar</button>
          <button onClick={salvar} disabled={salvando}
            className="px-4 py-1.5 bg-black text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CRMContatos;
